import { openDB, type IDBPDatabase } from "idb";
import type { ErasureDocument } from "../state/reducer";
import { isWord, wordText, type ArticleMeta, type PageElement } from "../state/types";

const DB_NAME = "erasure-db";
const STORE_NAME = "erasures";
const DB_VERSION = 1;

/** Summary of a stored erasure, for the splash screen list. */
export interface ErasureSummary {
  key: string;
  title?: string;
  url?: string;
}

/**
 * A single lazily-opened connection shared by every call.
 *
 * The previous implementation opened and closed a connection per operation, which
 * during a drag meant one open/write/close cycle per marked word. Holding one
 * connection also avoids the `versionchange` races that closing mid-transaction
 * can trigger.
 */
let connection: Promise<IDBPDatabase> | null = null;

const getDB = (): Promise<IDBPDatabase> => {
  if (!connection) {
    connection = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME);
        }
      },
    }).catch((error) => {
      // Let a later call retry rather than caching a rejected promise forever.
      connection = null;
      throw error;
    });
  }
  return connection;
};

/**
 * Closes the shared connection. Tests use this between cases; the app does not
 * need it, since the connection should live as long as the page.
 */
export const closeDB = async (): Promise<void> => {
  if (!connection) return;
  const db = await connection.catch(() => null);
  connection = null;
  db?.close();
};

/** A word as it is stored: its text, rather than an object per letter. */
interface StoredWord {
  type: "word";
  id: string;
  isMarked: boolean;
  text: string;
}

type StoredElement = PageElement | StoredWord;

interface StoredDocument extends Omit<ErasureDocument, "page"> {
  page: StoredElement[];
}

/**
 * A word's characters are derivable from its text, so they are not stored.
 *
 * Their ids are positional, and until the reader carves a word up none of them
 * is marked on its own — so all of it can be rebuilt from the text and the
 * word's id. Storing it anyway made a 4,600-word article 1.4MB of document, and
 * a save is a structured clone on the main thread: 162ms on a phone against
 * 25ms for the text alone, landing as a freeze a beat after every stroke. A
 * word the reader *has* carved up is stored whole, since its characters carry
 * marks of their own.
 */
const pack = (element: PageElement): StoredElement =>
  isWord(element) && !element.customCharacters
    ? {
        type: "word",
        id: element.id,
        isMarked: element.isMarked,
        text: wordText(element),
      }
    : element;

const unpack = (element: StoredElement): PageElement => {
  // Erasures saved before words were stored as text keep their characters.
  if (element.type !== "word" || "characters" in element) return element as PageElement;
  const { id, isMarked, text } = element;
  return {
    type: "word",
    id,
    isMarked,
    customCharacters: false,
    // `split("")` rather than spreading, to match the ids `textToPage` minted.
    characters: text.split("").map((character, index) => ({
      character,
      id: `${id}-${index}`,
      isMarked: false,
    })),
  };
};

export const saveErasure = async (document: ErasureDocument): Promise<void> => {
  const db = await getDB();
  const stored: StoredDocument = { ...document, page: document.page.map(pack) };
  await db.put(STORE_NAME, stored, document.key);
};

export const loadErasure = async (key: string): Promise<ErasureDocument | undefined> => {
  const db = await getDB();
  const stored: StoredDocument | undefined = await db.get(STORE_NAME, key);
  return stored && { ...stored, page: stored.page.map(unpack) };
};

export const listErasures = async (): Promise<ErasureSummary[]> => {
  const db = await getDB();
  const entries: ErasureDocument[] = await db.getAll(STORE_NAME);
  return entries
    .filter((entry): entry is ErasureDocument => Boolean(entry?.key))
    .map((entry) => ({
      key: entry.key,
      title: entry.meta?.title,
      url: entry.meta?.url,
    }));
};

export const deleteErasure = async (key: string): Promise<void> => {
  const db = await getDB();
  await db.delete(STORE_NAME, key);
};

export const clearErasures = async (): Promise<void> => {
  const db = await getDB();
  await db.clear(STORE_NAME);
};

export type { ArticleMeta };
